import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { approvalTheme, transition } from '../theme';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

interface ApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  className?: string;
}

export const ApprovalModal: React.FC<ApprovalModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'lg',
  showCloseButton = true,
  className,
}) => {
  const sizeStyles = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-[95vw] h-[95vh] flex flex-col',
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'bg-card border-border text-foreground p-0 gap-0',
          sizeStyles[size],
          className
        )}
        style={{
          boxShadow: approvalTheme.shadows.xl,
        }}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between p-6 border-b border-border">
          <div className="flex-1">
            <DialogTitle className="text-2xl font-bold text-foreground mb-1">
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="text-muted-foreground text-sm">
                {description}
              </DialogDescription>
            )}
          </div>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="ml-4 p-2 rounded-lg hover:bg-accent transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface ApprovalModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const ApprovalModalBody: React.FC<ApprovalModalBodyProps> = ({
  children,
  className,
}) => {
  return (
    <div className={cn('p-6', className)}>
      {children}
    </div>
  );
};

interface ApprovalModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const ApprovalModalFooter: React.FC<ApprovalModalFooterProps> = ({
  children,
  className,
}) => {
  return (
    <div className={cn('p-6 border-t border-border flex items-center justify-end gap-3', className)}>
      {children}
    </div>
  );
};

// Additional header and title components for compatibility
interface ApprovalModalHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const ApprovalModalHeader: React.FC<ApprovalModalHeaderProps> = ({
  children,
  className,
}) => {
  return (
    <div className={cn('flex items-start justify-between p-6 border-b border-border', className)}>
      {children}
    </div>
  );
};

interface ApprovalModalTitleProps {
  children: React.ReactNode;
  className?: string;
}

export const ApprovalModalTitle: React.FC<ApprovalModalTitleProps> = ({
  children,
  className,
}) => {
  return (
    <h2 className={cn('text-2xl font-bold text-foreground', className)}>
      {children}
    </h2>
  );
};
